/**
 * The assistant's opening bubble when the agent has no welcome message of its own. Mirrors the
 * public config default in `backend/app/core/constants/chat.py` (`DEFAULT_WELCOME_COPY`); the
 * public surfaces (widget, web app, mobile) receive the finished text from the API, so this is
 * for the console Playground preview only. The greeting is UI-only: it is never stored in the
 * conversation, never sent to the model, never billed.
 */
export type GreetingLanguage = "en" | "fil";

interface GreetingCopy {
  /** `{assistant}` and `{org}` are replaced. */
  named: string;
  /** Used when the assistant is named like the organization. */
  same: string;
}

export const DEFAULT_WELCOME_COPY: Readonly<Record<GreetingLanguage, GreetingCopy>> = {
  en: {
    named: "Hi! 👋 I'm {assistant} from {org}. Ask me anything about {org} — I'll answer from our official information.",
    same: "Hi! 👋 I'm the {org} assistant. Ask me anything about {org} — I'll answer from our official information.",
  },
  fil: {
    named: "Kumusta po! 👋 Ako si {assistant} mula sa {org}. Itanong n'yo po sa akin ang anumang tungkol sa {org} — sasagot po ako batay sa aming opisyal na impormasyon.",
    same: "Kumusta po! 👋 Ako ang assistant ng {org}. Itanong n'yo po sa akin ang anumang tungkol sa {org} — sasagot po ako batay sa aming opisyal na impormasyon.",
  },
};

/** Greeting language = the agent's primary language (Filipino → Filipino, everything else English). */
export function greetingLanguage(languages: readonly string[]): GreetingLanguage {
  return languages[0] === "fil" ? "fil" : "en";
}

export interface DefaultWelcomeInput {
  assistantName: string;
  orgName: string;
  languages: readonly string[];
}

export function defaultWelcome({ assistantName, orgName, languages }: DefaultWelcomeInput): string {
  const assistant = assistantName.trim();
  const org = orgName.trim();
  const copy = DEFAULT_WELCOME_COPY[greetingLanguage(languages)];
  const template = !assistant || assistant.toLowerCase() === org.toLowerCase() ? copy.same : copy.named;
  return template.replaceAll("{assistant}", assistant).replaceAll("{org}", org);
}

/** The configured welcome message, else the default. */
export function welcomeMessage(configured: unknown, fallback: DefaultWelcomeInput): string {
  return typeof configured === "string" && configured.trim() ? configured.trim() : defaultWelcome(fallback);
}
